import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BrokerMessageModel, EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { CompanyModel } from '@data-layer/models/companies';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { BehaviorSubject, interval, merge, ReplaySubject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { enumEntrySummaryState } from '@data-layer/enums/entry-summaries';
import { CompaniesService } from '@services/companies.service';
import { isEqual } from 'lodash-es';
import { AppService } from '@app/app.service';
import { enumUserBetaFlag } from '@data-layer/enums/user';

@Component({
  selector: 'es-chat-form-desktop',
  template: `
    <div class="chat-wrapper">
      <div class="chat-info" *ngIf="!!entrySummary && isShowInfo">
        <div class="chat-container-info-col">
          <div class="chat-info-col-header">{{ 'LABELS.EEZY_ID' | translate }}</div>
          <div class="chat-info-col-value">{{ entrySummary.referenceNumber }}</div>
        </div>

        <div class="chat-container-info-col">
          <div class="chat-info-col-header">{{ 'LABELS.CBP_NUM' | translate }}</div>
          <div class="chat-info-col-value">{{ entrySummary.entryNumber }}</div>
        </div>

        <div class="chat-container-info-col">
          <div class="chat-info-col-header">{{ 'STATUS_ES.SENT_TO_CBP' | translate }}</div>
          <div class="chat-info-col-value">{{ entrySummary.sentToProviderDate | customDateByUtc }}</div>
        </div>
      </div>

      <app-hts-assistant-history-link *ngIf="isShowHtsHistory" [entrySummaryId]="entrySummary.id"></app-hts-assistant-history-link>

      <app-broker-messages-chat
        [companyLogoUrl]="company.logoUri"
        [messages]="brokerMessages"
        (sent)="sentMessage($event)"
      ></app-broker-messages-chat>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./es-chat-desktop.component.scss'],
})
export class EsChatDesktopComponent implements OnInit, OnDestroy {
  @Input() public isShowInfo: boolean = false;

  public entrySummary: EntrySummaryModel;
  public company: CompanyModel;

  public brokerMessages: BrokerMessageModel[] = [];

  private afterSentMessage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private entrySummaryStateService: EntrySummaryStateService,
    private companiesService: CompaniesService,
    private cdr: ChangeDetectorRef,
    private appService: AppService,
  ) {}

  public ngOnInit(): void {
    this.entrySummaryStateService.entrySummary$
      .pipe(
        takeUntil(this.destroy$),
        filter((es) => !!es?.referenceNumber),
      )
      .subscribe((es) => {
        this.entrySummary = es;
        this.initPullingMessages();
      });

    this.company = this.companiesService.getCompany();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /** #13385: the classification assistant history, for tagged companies. */
  public get isShowHtsHistory(): boolean {
    return !!this.entrySummary && this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant);
  }

  public sentMessage(content: string) {
    this.entrySummaryStateService
      .sentMessageToBroker(content)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.afterSentMessage.next(true);
      });
  }

  private initPullingMessages(): void {
    merge(interval(60 * 1000), this.afterSentMessage)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.entrySummaryStateService.getBrokerMessages(false)),
      )
      .subscribe((brokerMessages: BrokerMessageModel[]) => {
        if (!isEqual(this.brokerMessages, brokerMessages)) {
          this.brokerMessages = brokerMessages;
          if (
            [
              enumEntrySummaryState.BROKER_PROCESSING_PENDING,
              enumEntrySummaryState.BROKER_PROCESSING_FAILED,
              enumEntrySummaryState.BROKER_PROCESSING_FINISHING,
            ].includes(this.entrySummary.state)
          ) {
            this.updateEntry();
          }
        }

        this.cdr.markForCheck();
      });
  }

  private updateEntry() {
    this.entrySummaryStateService
      .loadEntrySummary(this.entrySummary.referenceNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe((es) => {
        this.entrySummary = es;
        this.entrySummaryStateService.updateCurrentEntrySummary(es);
      });
  }
}
